import React, { useState, useRef } from "react";
import {
  StyleSheet,
  Platform,
  Modal,
  View,
  Text,
  TextInput,
  Pressable,
  Image,
  Animated,
  ScrollView,
  Dimensions,
  Alert,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { launchImageLibrary, launchCamera } from "react-native-image-picker";
import { RootState } from "../../../store";
import { createApiClient } from "../../../api/apiClient";
import { useToast } from "../../../components/LhToastContainer";
import { addImage } from "../../../store/slices/leadershipWalkRedirectSlice";
import LHProgressBar from "../../../components/LHProgressBar";
import FontFamily from "../../../constants/fonts";
import Svg, { Path } from "react-native-svg";
import { useTheme } from "../../../theme";

// Helper to check for web/document safely
const isWeb =
  typeof (globalThis as any).document !== "undefined" && Platform.OS === "web";

const EMPTY_TEXT = " ";

interface ImageData {
  imageUri: string | null;
  description: string;
}

interface LeadershipWalkRedirectActivityImageUploadProps {
  visible: boolean;
  onClose: () => void;
  activityId: string;
  activityLabel: string;
}

const LeadershipWalkRedirectActivityImageUpload: React.FC<
  LeadershipWalkRedirectActivityImageUploadProps
> = ({ visible, onClose, activityId, activityLabel }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const toast = useToast();
  const leadershipImages = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.images
  );
  const walkState = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.walkState
  );

  // Component state
  const [images, setImages] = useState<ImageData[]>([
    { imageUri: null, description: activityLabel },
    { imageUri: null, description: activityLabel },
  ]);
  const [errorMessages, setErrorMessages] = useState<(string | null)[]>([
    null,
    null,
  ]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([0, 0]);
  const [uploadingStates, setUploadingStates] = useState<boolean[]>([
    false,
    false,
  ]);

  // Create animated values for each image
  const iconScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const circleScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const borderColors = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
  ]).current;

  // Helper function to check if device is a small mobile device
  const isSmallMobileDevice = () => {
    const windowWidth = Dimensions.get("window").width;
    const windowHeight = Dimensions.get("window").height;
    return (
      (Platform.OS === "ios" || Platform.OS === "android") &&
      (windowWidth < 380 || windowHeight < 700)
    );
  };

  // Helper function to check if device is iOS mobile (not iPad)
  const isIOSMobile = () => {
    const windowWidth = Dimensions.get("window").width;
    const windowHeight = Dimensions.get("window").height;
    const isSmallScreen = windowWidth < 768 && windowHeight < 1024;
    return Platform.OS === "ios" && isSmallScreen;
  };

  // Get appropriate container heights based on device size
  const getImageContainerHeight = () => {
    if (isSmallMobileDevice()) {
      return 110; // Smaller height for very small devices
    }
    return 150; // Default height
  };

  const isCompactMode = isSmallMobileDevice();
  const imageContainerHeight = getImageContainerHeight();

  // Reset state and load images when activityId changes or component becomes visible
  React.useEffect(() => {
    if (visible) {
      console.log("LeadershipWalkRedirectActivityImageUpload - activityId:", activityId);
      if (!activityId) {
        console.error(
          "Invalid activityId provided to LeadershipWalkRedirectActivityImageUpload"
        );
        return;
      }
      // Find existing images for this activity by observationId and sort by imageId
      const existingImages = leadershipImages
        .filter((img) => img.observationId === activityId)
        .sort((a, b) => {
          const idA = parseInt(a.imageId, 10);
          const idB = parseInt(b.imageId, 10);
          return idA - idB;
        });
      // Make sure we have the correct images for slots 1 and 2
      const image1 = existingImages.find((img) => String(img.imageId) === "1");
      const image2 = existingImages.find((img) => String(img.imageId) === "2");
      // Update local state with fresh data for this activity
      setImages([
        {
          imageUri: image1?.imagePreview || image1?.imageUrl || null,
          description:
            image1 && typeof image1.description !== "undefined"
              ? image1.description
              : activityLabel,
        },
        {
          imageUri: image2?.imagePreview || image2?.imageUrl || null,
          description:
            image2 && typeof image2.description !== "undefined"
              ? image2.description
              : activityLabel,
        },
      ]);
      setErrorMessages([null, null]);
      setUploadProgress([0, 0]);
      setUploadingStates([false, false]);
    }
  }, [activityId, visible, leadershipImages, activityLabel]);

  // Function to handle hover state for web with smooth animations
  const handleHoverIn = (index: number) => {
    if (isWeb && walkState !== "completed") {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1.2,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1.1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  const handleHoverOut = (index: number) => {
    if (isWeb && walkState !== "completed") {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 0,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Helper function to handle mobile image selection (camera or gallery)
  const handleMobileImageSelection = async (
    index: number,
    source: "camera" | "gallery"
  ) => {
    const options = {
      mediaType: "photo" as const,
      quality: 1 as const,
    };

    let result;
    try {
      if (source === "camera") {
        result = await launchCamera(options);
      } else {
        result = await launchImageLibrary(options);
      }
    } catch (error: any) {
      console.error("Error launching camera/library:", error);

      // Handle permission errors gracefully
      if (error.code === "camera_unavailable" || error.code === "permission") {
        Alert.alert(
          "Permission Required",
          source === "camera"
            ? "Camera access is required to take photos. Please enable camera permissions in Settings."
            : "Photo library access is required. Please enable photo permissions in Settings.",
          [{ text: "OK" }]
        );
      } else {
        Alert.alert(
          "Error",
          `Unable to ${
            source === "camera" ? "open camera" : "access photo library"
          }. Please try again.`,
          [{ text: "OK" }]
        );
      }

      // Reset uploading state
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.didCancel) {
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.errorMessage) {
      console.error("Image picker error:", result.errorMessage);
      Alert.alert("Error", result.errorMessage, [{ text: "OK" }]);
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.assets && result.assets.length > 0) {
      const asset = result.assets[0];
      if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) {
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = "File size exceeds 10MB limit";
          return newErrors;
        });
        setUploadingStates((prev) => {
          const newStates = [...prev];
          newStates[index] = false;
          return newStates;
        });
        return;
      }

      const formData = new FormData();
      const fileName = asset.fileName || "image.jpg";
      formData.append("image", {
        uri: asset.uri,
        name: fileName,
        type: asset.type || "image/jpeg",
      } as any);
      formData.append("containerName", "leadership"); // Add containerName for LeadershipWalk

      try {
        const api = createApiClient();
        const response = await api.post("/common/images/upload", formData, {
          headers: {
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent && progressEvent.total) {
              const progress = Math.min(
                0.5,
                progressEvent.loaded / progressEvent.total
              );
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = progress;
                return arr;
              });
            }
          },
        });

        if (response && response.data && response.data.success === true) {
          const { imageUrl, fileName: responseFileName } = response.data.data;

          // Update local state immediately to show image
          const updatedImages = [...images];
          updatedImages[index] = {
            imageUri: imageUrl,
            description: updatedImages[index].description, // Preserve existing description
          };
          setImages(updatedImages);

          // Update Redux state with the new image
          const imageData = {
            imageId: `${index + 1}`,
            fileName: responseFileName,
            imagePreview: imageUrl,
            imageUrl: imageUrl,
            description:
              images[index].description === "" ||
              images[index].description === EMPTY_TEXT
                ? EMPTY_TEXT
                : images[index].description,
            observationId: activityId,
          };

          dispatch(addImage(imageData));

          // Show success toast notification
          toast.showToast({
            header: "Success",
            subtext: "Image uploaded successfully",
            variant: "success",
          });

          // Set progress to 100% only after successful response
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 1;
            return arr;
          });
          // Show 100% briefly before hiding progress bar
          await new Promise((resolve) => setTimeout(resolve, 500));
        } else {
          // Get error message from response or use default
          const errorMessage =
            response?.data?.message || "Failed to upload image";

          // Set error message in state
          setErrorMessages((prev) => {
            const newErrors = [...prev];
            newErrors[index] = "Unable to upload image";
            return newErrors;
          });

          // Show error toast notification
          toast.showToast({
            header: "Error Uploading Image",
            subtext: errorMessage,
            variant: "error",
          });
        }
      } catch (error: any) {
        console.error("Image upload error:", error);

        // Set error message in state
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = "Unable to upload image";
          return newErrors;
        });

        // Show error toast notification
        toast.showToast({
          header: "Error Uploading Image",
          subtext: error?.message || "An unexpected error occurred",
          variant: "error",
        });
      } finally {
        // Only reset progress and uploading state if there was no error
        if (!errorMessages[index]) {
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 0;
            return arr;
          });

          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = false;
            return newStates;
          });
        } else {
          // If there was an error, we still need to allow retry
          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = false;
            return newStates;
          });
        }
      }
    } else {
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "Unable to upload image";
        return newErrors;
      });
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
    }
  };

  const handleImageUpload = async (index: number) => {
    // Prevent upload only if in completed state or already uploading
    if (walkState === "completed" || uploadingStates[index]) {
      return;
    }

    // Reset error for this image
    setErrorMessages((prev) => {
      const newErrors = [...prev];
      newErrors[index] = null;
      return newErrors;
    });

    // Reset progress
    setUploadProgress((prev) => {
      const arr = [...prev];
      arr[index] = 0;
      return arr;
    });

    // Update uploading state
    setUploadingStates((prev) => {
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });

    try {
      if (isWeb) {
        const input = (globalThis as any).document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (event: any) => {
          const file = event.target.files[0];
          if (file) {
            // Check file size (5MB limit)
            if (file.size > 10 * 1024 * 1024) {
              setErrorMessages((prev) => {
                const newErrors = [...prev];
                newErrors[index] = "File size exceeds 10MB limit";
                return newErrors;
              });
              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
              return;
            }

            const formData = new FormData();
            formData.append("image", file);
            formData.append("containerName", "leadership"); // Add containerName for LeadershipWalk

            try {
              const api = createApiClient();
              const response = await api.post(
                "/common/images/upload",
                formData,
                {
                  headers: {
                    Accept: "application/json",
                  },
                  onUploadProgress: (progressEvent: any) => {
                    if (progressEvent && progressEvent.total) {
                      const progress = Math.min(
                        0.5,
                        progressEvent.loaded / progressEvent.total
                      );
                      setUploadProgress((prev) => {
                        const arr = [...prev];
                        if (prev[index] === 0) {
                          arr[index] = Math.max(0.05, progress);
                        } else {
                          arr[index] = progress;
                        }
                        return arr;
                      });
                    }
                  },
                }
              );

              if (response && response.data && response.data.success === true) {
                const { imageUrl, fileName } = response.data.data;
                // Update local state
                const newImages = [...images];
                newImages[index] = {
                  imageUri: imageUrl,
                  description: newImages[index].description, // Preserve existing description
                };
                setImages(newImages);
                // Prepare image data for Redux
                if (!activityId) {
                  console.error(
                    "Invalid activityId when updating image in Redux"
                  );
                  return;
                }
                const imageData = {
                  observationId: activityId,
                  imageId: `${index + 1}`,
                  fileName: fileName,
                  imagePreview: imageUrl,
                  imageUrl: imageUrl,
                  description:
                    images[index].description === "" ||
                    images[index].description === EMPTY_TEXT
                      ? EMPTY_TEXT
                      : images[index].description,
                };
                dispatch(addImage(imageData));
                toast.showToast({
                  header: "Success",
                  subtext: "Image uploaded successfully",
                  variant: "success",
                });
                // Set progress to 100% after successful upload
                setUploadProgress((prev) => {
                  const arr = [...prev];
                  arr[index] = 1;
                  return arr;
                });

                // Show 100% briefly before hiding progress bar
                await new Promise((resolve) => setTimeout(resolve, 500));
              }
            } catch (error) {
              console.error("Image upload error:", error);
              setErrorMessages((prev) => {
                const newErrors = [...prev];
                newErrors[index] = "Failed to upload image";
                return newErrors;
              });

              toast.showToast({
                header: "Upload Failed",
                subtext: "Failed to upload image. Please try again.",
                variant: "error",
              });
            }
          }
        };
        input.click();
      } else {
        // Mobile: Show action sheet with Take Photo and Choose from Gallery options
        Alert.alert("Add Image", "How would you like to add an image?", [
          {
            text: "Take Photo",
            onPress: () => handleMobileImageSelection(index, "camera"),
          },
          {
            text: "Choose from Gallery",
            onPress: () => handleMobileImageSelection(index, "gallery"),
          },
          {
            text: "Cancel",
            style: "cancel",
            onPress: () => {
              // Reset uploading state when cancelled
              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            },
          },
        ]);
      }
    } catch (error) {
      console.error("Image upload error:", error);
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "Failed to upload image";
        return newErrors;
      });

      toast.showToast({
        header: "Upload Failed",
        subtext: "Failed to upload image. Please try again.",
        variant: "error",
      });
    } finally {
      // Only reset progress and uploading state if there was no error
      if (!errorMessages[index]) {
        setUploadProgress((prev) => {
          const arr = [...prev];
          arr[index] = 0;
          return arr;
        });
      }

      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
    }
  };

  const handleDescriptionChange = (index: number, text: string) => {
    // Prevent changes if in completed state
    if (walkState === "completed") {
      return;
    }

    const updatedImages = [...images];
    updatedImages[index].description = text === "" ? EMPTY_TEXT : text;
    setImages(updatedImages);

    // Update Redux if the image exists
    if (images[index].imageUri) {
      // Find existing image data for this activity and index
      const existingImage = leadershipImages.find(
        (img) =>
          img.observationId === activityId && img.imageId === `${index + 1}`
      );

      if (existingImage) {
        const imageData = {
          ...existingImage,
          description: text === "" ? EMPTY_TEXT : text,
        };

        console.log("Updating image description in Redux:", {
          observationId: activityId,
          imageId: `${index + 1}`,
          description: text,
          existingImageData: existingImage,
        });

        dispatch(addImage(imageData));
      }
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }] }>
        <View
          style={[
            styles.modalContent,
            isCompactMode && styles.modalContentCompact,
            {
              backgroundColor: isDarkMode ? colors.background.surface : "white",
              borderColor: isDarkMode ? colors.border.default : "#D8E7FB",
            },
          ]}
        >
          <View style={styles.modalHeader}>
            <Text
              style={[
                styles.modalTitle,
                isCompactMode && styles.modalTitleCompact,
                { color: isDarkMode ? colors.text.inverse : colors.text.primary },
              ]}
            >
              Upload Images for {activityLabel}
            </Text>
            <Pressable style={styles.closeButton} onPress={onClose}>
              <Text style={{ color: isDarkMode ? colors.text.inverse : colors.text.primary }}>✕</Text>
            </Pressable>
          </View>

          <ScrollView
            contentContainerStyle={[
              styles.container,
              isCompactMode && styles.containerCompact,
              { backgroundColor: isDarkMode ? colors.background.surface : "#fff" },
            ]}
            showsVerticalScrollIndicator={true}
          >
            {[0, 1].map((index) => {
              // Determine if image exists and if it's in completed state
              const hasImage = !!images[index].imageUri;
              const isCompleted = walkState === "completed";

              return (
                <View
                  key={index}
                  style={[
                    styles.observationContainer,
                    isIOSMobile() && styles.observationContainerCompact,
                  ]}
                >
                  <View
                    style={[
                      styles.uploadContainer,
                      { height: imageContainerHeight },
                      isCompleted && styles.disabledContainer,
                      isIOSMobile() && styles.uploadContainerCompact,
                    ]}
                  >
                    <Text
                      style={[
                        styles.uploadHeader,
                        isIOSMobile() && { fontSize: 12, marginBottom: 4 },
                        { color: isDarkMode ? colors.text.inverse : "#333333" },
                      ]}
                    >
                      Image Upload
                    </Text>
                    {hasImage ? (
                      // Full image preview - using full container
                      <Pressable
                        style={[
                          styles.imagePreviewContainer,
                          isCompleted && styles.disabledContainer,
                        ]}
                        onPress={() => !isCompleted && handleImageUpload(index)}
                        disabled={isCompleted}
                      >
                        <View style={styles.imageContainer}>
                          <Image
                            source={{ uri: images[index].imageUri! }}
                            style={styles.fullImage}
                          />
                        </View>
                        {!isCompleted && (
                          <View style={styles.imageOverlay}></View>
                        )}
                      </Pressable>
                    ) : (
                      // Upload area - matching AttachmentUploader style
                      <View style={styles.attachmentsContainer}>
                        <View
                          style={[
                            styles.dropZone,
                            uploadingStates[index] && styles.dropZoneUploading,
                            errorMessages[index] && styles.dropZoneError,
                            isCompleted && styles.dropZoneDisabled,
                            isDarkMode && {
                              backgroundColor: colors.background.elevated,
                              borderColor: colors.border.default,
                            },
                          ]}
                        >
                          {uploadingStates[index] &&
                          uploadProgress[index] > 0 ? (
                            <View style={styles.progressContainer}>
                              <LHProgressBar
                                progress={uploadProgress[index]}
                                height={16}
                              />
                              <Text
                                style={[
                                  styles.progressText,
                                  { color: isDarkMode ? colors.text.inverse : "#3A3A3A" },
                                ]}
                              >
                                {uploadProgress[index] >= 0.99
                                  ? "Processing..."
                                  : `${Math.round(
                                      uploadProgress[index] * 100
                                    )}%`}
                              </Text>
                            </View>
                          ) : (
                            <>
                              <View style={styles.iconLabelContainer}>
                                <View style={styles.iconContainer}>
                                  <Svg
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <Path
                                      d="M21.3333 2.66667V21.3333H2.66667V2.66667H21.3333ZM21.3333 0H2.66667C1.2 0 0 1.2 0 2.66667V21.3333C0 22.8 1.2 24 2.66667 24H21.3333C22.8 24 24 22.8 24 21.3333V2.66667C24 1.2 22.8 0 21.3333 0ZM14.8533 11.8133L10.8533 16.9733L8 13.52L4 18.6667H20L14.8533 11.8133Z"
                                      fill={
                                        errorMessages[index]
                                          ? "red"
                                          : isDarkMode
                                          ? colors.text.inverse
                                          : "#3A3A3A"
                                      }
                                    />
                                  </Svg>
                                </View>
                                <Text
                                  style={[
                                    styles.dragDropText,
                                    errorMessages[index] && styles.errorText,
                                    !errorMessages[index] && {
                                      color: isDarkMode ? colors.text.inverse : "#3A3A3A",
                                    },
                                  ]}
                                >
                                  {errorMessages[index] ||
                                    "Drag and drop your file here"}
                                </Text>
                                <Text
                                  style={[
                                    styles.dragDropText,
                                    { color: isDarkMode ? colors.text.inverse : "#3A3A3A" },
                                  ]}
                                >
                                  (only .jpg,.png)
                                </Text>
                              </View>
                              <View style={{ marginTop: 8 }}>
                                <Pressable
                                  style={[
                                    styles.uploadButton,
                                    isCompleted && styles.disabledButton,
                                    isDarkMode && {
                                      backgroundColor: colors.background.surface,
                                      borderColor: colors.brand.primary,
                                    },
                                  ]}
                                  onPress={() =>
                                    !isCompleted && handleImageUpload(index)
                                  }
                                  disabled={
                                    isCompleted || uploadingStates[index]
                                  }
                                >
                                  <View style={styles.buttonContent}>
                                    <View style={styles.uploadIconContainer}>
                                      <Svg
                                        width="10"
                                        height="12"
                                        viewBox="0 0 10 12"
                                        fill="none"
                                      >
                                        <Path
                                          d="M3 8.66667H7V4.66667H9.66667L5 0L0.333336 4.66667H3V8.66667ZM5 1.88667L6.44667 3.33333H5.66667V7.33333H4.33334V3.33333H3.55334L5 1.88667ZM0.333336 10H9.66667V11.3333H0.333336V10Z"
                                          fill={
                                            isCompleted
                                              ? "#999"
                                              : colors.brand.primary
                                          }
                                        />
                                      </Svg>
                                    </View>
                                    <Text
                                      style={[
                                        styles.uploadButtonText,
                                        {
                                          color: isDarkMode
                                            ? colors.text.inverse
                                            : colors.brand.primary,
                                        },
                                        isCompleted &&
                                          styles.disabledButtonText,
                                      ]}
                                    >
                                      {uploadingStates[index]
                                        ? "Uploading..."
                                        : "Upload File"}
                                    </Text>
                                  </View>
                                </Pressable>
                              </View>
                            </>
                          )}
                        </View>
                      </View>
                    )}
                  </View>

                  <View
                    style={[
                      styles.descriptionContainer,
                      { height: imageContainerHeight },
                      isIOSMobile() && styles.descriptionContainerCompact,
                    ]}
                  >
                    <Text
                      style={[
                        styles.descriptionHeader,
                        isIOSMobile() && { fontSize: 12, marginBottom: 4 },
                        { color: isDarkMode ? colors.text.inverse : "#333333" },
                      ]}
                    >
                      Description
                    </Text>
                    <TextInput
                      style={[
                        styles.descriptionInput,
                        walkState === "completed" && styles.disabledInput,
                        isCompactMode && { padding: 4, fontSize: 12 },
                        {
                          borderColor: isDarkMode ? colors.border.default : "#ced4da",
                          backgroundColor: isDarkMode
                            ? colors.background.elevated
                            : "#fff",
                          color: isDarkMode ? colors.text.inverse : colors.text.primary,
                        },
                      ]}
                      value={
                        images[index].description === EMPTY_TEXT
                          ? ""
                          : images[index].description
                      }
                      onChangeText={(text) =>
                        handleDescriptionChange(index, text)
                      }
                      multiline
                      numberOfLines={4}
                      editable={walkState !== "completed"}
                      placeholder="Add description..."
                      placeholderTextColor={isDarkMode ? colors.text.secondary : "#c0c0c0"}
                    />
                  </View>
                </View>
              );
            })}

            <View style={styles.buttonContainer}>
              <Pressable
                style={[styles.cancelButton, isCompactMode && { padding: 6 }]}
                onPress={onClose}
              >
                <Text
                  style={[
                    styles.cancelButtonText,
                    isCompactMode && { fontSize: 14 },
                    { color: isDarkMode ? colors.text.inverse : colors.brand.primary },
                  ]}
                >
                  Cancel
                </Text>
              </Pressable>

              <Pressable
                style={[
                  styles.proceedButton,
                  isCompactMode && { padding: 6 },
                  { backgroundColor: colors.brand.primary },
                ]}
                onPress={onClose}
              >
                <Text
                  style={[
                    styles.proceedButtonText,
                    isCompactMode && { fontSize: 14 },
                    { color: colors.text.inverse },
                  ]}
                >
                  Proceed
                </Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 8,
    borderWidth: 10,
    borderColor: "#D8E7FB",
    padding: 16,
    width: Platform.OS === "web" ? "80%" : "90%",
    maxWidth: 800,
    maxHeight:
      Platform.OS === "ios" || Platform.OS === "android" ? "80%" : "90%",
  },
  modalContentCompact: {
    padding: 8,
    width: "90%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    fontFamily: FontFamily.bold,
  },
  modalTitleCompact: {
    fontSize: 16,
  },
  closeButton: {
    padding: 8,
  },
  container: {
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 8,
  },
  containerCompact: {
    padding: 8,
  },
  observationContainer: {
    flexDirection: "row", // Default to row for web and iPad
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: 20,
    position: "relative",
  },
  observationContainerCompact: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  uploadContainer: {
    width: "35%", // Default width for web and iPad
    height: 150,
    marginRight: 16,
    marginBottom: 0,
    overflow: "visible", // Changed from 'hidden' to prevent border cropping
    position: "relative",
  },
  uploadContainerCompact: {
    width: "100%", // Full width for iOS mobile
    height: 110,
    marginRight: 0,
    marginBottom: 16,
  },
  // AttachmentUploader-style components
  attachmentsContainer: {
    width: "100%",
    flex: 1, // Use flex to fill available space
  },
  dropZone: {
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 110,
    backgroundColor: "#F5F9FF",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#D8E7FB",
    borderRadius: 8,
  },
  dropZoneUploading: {
    borderColor: "#0066CC",
    backgroundColor: "rgba(0,102,204,0.04)",
  },
  dropZoneError: {
    borderColor: "red",
    backgroundColor: "rgba(255, 235, 235, 0.8)",
  },
  dropZoneDisabled: {
    backgroundColor: "#D0D0D0",
    borderColor: "#A0A0A0",
  },
  iconLabelContainer: {
    width: "100%",
    alignItems: "center",
  },
  iconContainer: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    marginTop: -8,
  },
  dragDropText: {
    fontFamily: FontFamily.regular,
    fontWeight: "400",
    fontSize: 12,
    lineHeight: 18,
    textAlign: "center",
    color: "#3A3A3A",
    width: "100%",
    marginTop: 4,
  },
  uploadButton: {
    height: 24,
    backgroundColor: "#FBFCFE",
    borderWidth: 1,
    borderColor: "#005CBC",
    borderRadius: 24,
    alignSelf: "stretch",
  },
  disabledButton: {
    backgroundColor: "#D0D0D0",
    borderColor: "#999",
  },
  buttonContent: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 2,
    paddingHorizontal: 8,
    paddingVertical: 0,
  },
  uploadIconContainer: {
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  uploadButtonText: {
    fontFamily: FontFamily.regular,
    fontWeight: "700",
    fontSize: 12,
    lineHeight: 18,
    textAlign: "center",
    color: "#005CBC",
  },
  disabledButtonText: {
    color: "#999",
  },
  attachmentPreview: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: Platform.OS === "web" ? 12 : 16,
    borderWidth: 1,
    borderColor: "#e9ecef",
    borderRadius: 8,
    backgroundColor: "#f8f9fa",
    width: "100%",
    minHeight: Platform.OS === "ios" ? 60 : 50,
  },
  fileInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  previewImage: {
    width: 40,
    height: 40,
    borderRadius: 4,
    resizeMode: "cover",
  },
  fileName: {
    marginLeft: 8,
    fontSize: Platform.OS === "web" ? 14 : 16,
    color: "#212529",
    flex: 1,
    fontFamily: FontFamily.regular,
  },
  removeButton: {
    padding: Platform.OS === "web" ? 4 : 8,
    marginLeft: 8,
  },
  descriptionContainer: {
    flex: 1, // Default flex for web and iPad
    width: undefined,
    height: 150,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginTop: 0,
  },
  descriptionContainerCompact: {
    flex: undefined, // Remove flex for iOS mobile
    width: "100%", // Full width for iOS mobile
    height: 110,
    marginTop: 24, // Increased margin top for iOS mobile to prevent overlapping
  },
  uploadHeader: {
    fontSize: 14,
    fontWeight: "700",
    color: "#333333",
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
  descriptionHeader: {
    fontSize: 14,
    fontWeight: "700",
    color: "#333333",
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
  descriptionInput: {
    width: "100%",
    flex: 1,
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: 8,
    fontSize: 14,
    backgroundColor: "#fff",
    textAlignVertical: "top",
    fontFamily: FontFamily.regular,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 16,
    alignSelf: "stretch",
    marginTop: 16,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 4,
    paddingVertical: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButtonText: {
    fontFamily: FontFamily.regular,
    fontWeight: "700",
    fontSize: 14,
    lineHeight: 20,
    color: "#005CBC",
  },
  proceedButton: {
    height: 32,
    backgroundColor: "#005CBC",
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  proceedButtonText: {
    fontFamily: FontFamily.regular,
    fontWeight: "700",
    fontSize: 14,
    lineHeight: 20,
    color: "#FFFFFF",
  },
  fullWidth: {
    width: "100%",
  },
  disabledText: {
    color: "#DDD",
  },
  errorText: {
    color: "red",
  },
  progressContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  progressText: {
    fontSize: 12,
    marginTop: 8,
    fontFamily: FontFamily.regular,
    color: "#3A3A3A",
  },
  disabledContainer: {
    opacity: 0.6,
    backgroundColor: "#f2f2f2",
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  disabledInput: {
    backgroundColor: "#D0D0D0",
    color: "#888888",
  },
  imagePreviewContainer: {
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    width: "100%",
    flex: 1, // Use flex to fill available space instead of fixed height
  },
  imageContainer: {
    width: "100%",
    height: "100%",
  },
  fullImage: {
    width: "100%",
    height: "100%",
    resizeMode: "contain",
  },
  imageOverlay: {},
  overlayText: {
    color: "white",
    fontSize: 12,
    marginLeft: 4,
  },
});

export default LeadershipWalkRedirectActivityImageUpload;
